import { autoinject } from 'aurelia-framework';
import { AccountService } from 'app/core/services/account-service';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { State } from 'app/shared/lib/state';
import { AppLogger } from 'app/shared/lib/logging/app-logger';

export enum AccountPermissions {
    ApplicationAdmin = 'Core.Application.Storage.app-admin'
}

@autoinject()
export class AccountContext {

    public state: State = new State();

    private userContextId: string = ''; // used to check changes in user context between calls to load()

    private permissions: Set<string> = new Set<string>();
    private routes: Set<string> = new Set<string>();
    private templates: Map<string, string> = new Map<string, string>();
    private loadDataPromise: Promise<void> | null = null;

    constructor(private readonly service: AccountService, private readonly env: EnvironmentService, private readonly logger: AppLogger) {
        this.logger.source = this;
        this.logger.target = 'console';
    }

    public load(): Promise<void> {
        // return cached loaded promise
        if(this.loadDataPromise) { return this.loadDataPromise; }
        return (this.loadDataPromise = this.loadData());
    }

    private async loadData(): Promise<void> {
        const newUserContextId = this.env.context.id || '';
        if(this.userContextId !== newUserContextId) {
            this.logger.info(`Loading Permissions/Pages for Tenant '${this.env.tenant.description}', User '${this.env.user.description}' (Context ${this.env.context.id})`);
            await Promise.all([this.loadRoutes(), this.loadPermissions()]);
            this.userContextId = newUserContextId;
        }
    }

    public hasPermission(code: string): boolean {
        const result = this.permissions.has(code);
        this.logger.debug(`Has Permission '${code}': ${result}`);
        return result;
    }

    /**
     * Returns `true` if the current account has access to the given route
     * @param route route's `name` or transitional page's `hyperlinkId`
     */
    public hasRouteAccess(route: string): boolean {
        const result = this.routes.has(route);
        this.logger.debug(`Has Route Access'${route}': ${result}`);
        return result;
    }

    public getPageTemplate(pageId: string): string {
        return this.templates.get(pageId) || '';
    }

    public clear(): void {
        this.userContextId = '';
        this.permissions.clear();
        this.routes.clear();
        this.templates.clear();
        this.loadDataPromise = null;
        this.logger.debug('Clearing Permissions/Routes');
    }

    private async loadRoutes(): Promise<void> {
        try {
            this.state.beginLoad();

            this.routes.clear();
            const response = await this.service.loadPages();

            if(response.isSuccessful) {
                response.results
                    .map(page => page.code || page.id)
                    .forEach(code => code && this.routes.add(code));
                response.results
                    .filter(page => (<any>page).template) // TODO: remove <any>
                    .forEach(page => this.templates.set(page.id || '', (<any>page).template.id || ''));
            }
        }
        catch(error) {
            this.logger.error('Unable to load Routes', error);
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    private async loadPermissions(): Promise<void> {
        try {
            this.state.beginLoad();

            this.permissions.clear();
            const response = await this.service.loadPermissions();

            if(response.isSuccessful) {
                response.results
                    .map(permission => permission.code)
                    .forEach(code => code && this.permissions.add(code));
            }
        }
        catch(error) {
            this.logger.error('Unable to load Permissions', error);
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
